import {
  AVATAR_PALETTE,
  GRID_SIZE,
  EXPECTED_PIXEL_STRING_LENGTH,
} from "./avatarPalette";

const PALETTE = [
  "#c026d3", // fuchsia
  "#48ebe2", // teal
  "#7c3aed", // violet
  "#2563eb", // blue
  "#0891b2", // cyan
  "#059669", // emerald
  "#65a30d", // lime
  "#d97706", // amber
  "#dc2626", // red
  "#db2777", // pink
  "#9333ea", // purple
  "#f97316", // orange
];

function djb2(str) {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) + hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

const HEX_PATTERN = /^[0-9a-f]+$/;
const customAvatars = new Map();
let bufferCanvas = null;

function isValidPixelString(pixels) {
  return (
    typeof pixels === "string" &&
    pixels.length === EXPECTED_PIXEL_STRING_LENGTH &&
    HEX_PATTERN.test(pixels)
  );
}

function renderPixelsToDataUrl(pixels) {
  if (typeof document === "undefined") return null;
  if (!bufferCanvas) {
    bufferCanvas = document.createElement("canvas");
    bufferCanvas.width = GRID_SIZE;
    bufferCanvas.height = GRID_SIZE;
  }
  const ctx = bufferCanvas.getContext("2d");
  ctx.fillStyle = AVATAR_PALETTE[0];
  ctx.fillRect(0, 0, GRID_SIZE, GRID_SIZE);
  for (let i = 0; i < EXPECTED_PIXEL_STRING_LENGTH; i++) {
    const idx = parseInt(pixels[i], 16);
    if (!idx) continue;
    const x = i % GRID_SIZE;
    const y = Math.floor(i / GRID_SIZE);
    ctx.fillStyle = AVATAR_PALETTE[idx];
    ctx.fillRect(x, y, 1, 1);
  }
  return bufferCanvas.toDataURL("image/png");
}

function dispatchUpdated(username) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("scrawl-avatar-updated", { detail: { username } })
  );
}

export function setCustomAvatar(username, pixels) {
  if (!username || !isValidPixelString(pixels)) return;
  const existing = customAvatars.get(username);
  if (existing && existing.pixels === pixels) return;
  const dataUrl = renderPixelsToDataUrl(pixels);
  if (!dataUrl) return;
  customAvatars.set(username, { pixels, dataUrl });
  dispatchUpdated(username);
}

export function clearCustomAvatar(username) {
  if (!username || !customAvatars.has(username)) return;
  customAvatars.delete(username);
  dispatchUpdated(username);
}

export function hasCustomAvatar(username) {
  return customAvatars.has(username);
}

// Generates a deterministic 5×5 symmetric identicon SVG for a given username.
// Same username always produces the same avatar across all devices and sessions.
export function getAvatar(username) {
  const cached = username ? customAvatars.get(username) : null;
  if (cached) return cached.dataUrl;

  const size = 100;
  const cell = size / 5;
  const h = djb2(username || "user");
  const color = PALETTE[h % PALETTE.length];

  // Column mirror map: [0,1,2,1,0] — left half mirrors right half
  let rects = `<rect width="${size}" height="${size}" fill="#f5f0fd"/>`;
  for (let row = 0; row < 5; row++) {
    for (let col = 0; col < 5; col++) {
      const sourceCol = col > 2 ? 4 - col : col;
      const bit = (h >> (row * 3 + sourceCol)) & 1;
      if (bit) {
        rects += `<rect x="${col * cell}" y="${
          row * cell
        }" width="${cell}" height="${cell}" fill="${color}"/>`;
      }
    }
  }

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}">${rects}</svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}
